import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { SortOrder } from "antd/es/table/interface";
import { IAppUser, IInvoice } from "../../../../interfaces";
import InvoicesService from "../../../../services/http/services/InvoicesService";
import AppUsersService from "../../../../services/http/services/AppUsersService";
import useAuth from "../../../../hooks/useAuth";
import { EmitError } from "../../../../utils/EmitError";
import { formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import { formatCurrency, formatPoints } from "../../../../utils/Masks";
import StatusTag from "../../components/StatusTag";
import useModules from "../../../../hooks/useModules";
import { useGetColumnSearchProps } from "../../../../hooks/FilterTable/stringClearFiltros";
import { useGetColumnSearchPropsData } from "../../../../hooks/FilterTable/date";

interface IPeriod {
    month: number,
    year: number,
}

interface ITableData extends IInvoice {
    key: number;
}

export default function useInvoices() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [appUser, setAppUser] = useState<IAppUser>();
    const [period, setPeriod] = useState<IPeriod | undefined>(undefined);
    const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
    const refFuncaoLimparFiltrosString = useRef<any>(null);


    const { activeCity } = useAuth();
    const { hasRewardModule } = useModules();
    const { id, period: paramsPeriod } = useParams();

    const columns = [
        {
            title: 'Empresa',
            dataIndex: 'company',
            key: 'company',
            sorter: (a: ITableData, b: ITableData) => a?.company?.localeCompare(b?.company || '')!,
            showSorterTooltip: false,
            ...useGetColumnSearchProps({dataIndex: 'company', ref: refFuncaoLimparFiltrosString}),
        },
        {
            title: 'Valor Total',
            dataIndex: 'value',
            key: 'value',
            render: (record: number) => record ? `R$ ${formatCurrency(record)}` : 'R$ 0,00',
            sorter: (a: ITableData, b: ITableData) => (a?.value || 0) - (b?.value || 0),
            showSorterTooltip: false,
        },
        {
            title: 'Total de Pontos',
            dataIndex: 'reward_points',
            key: 'reward_points',
            render: (record: number) => formatPoints(record),
            sorter: (a: ITableData, b: ITableData) => (a?.reward_points || 0) - (b?.reward_points || 0),
            showSorterTooltip: false,
            hidden: !hasRewardModule,
        },
        {
            title: 'Status',
            dataIndex: 'is_valid',
            key: 'is_valid',
            sorter: (a: ITableData, b: ITableData) => a.status.localeCompare(b.status),
            showSorterTooltip: false,
            filters: [
                {
                    text: 'Concluído',
                    value: 'Concluído',
                },
                {
                    text: 'Com erro',
                    value: 'Erro',
                },
                {
                    text: 'Alerta',
                    value: 'Alerta',
                },
            ],
            onFilter: (value: any, record: ITableData) => {
                return record.status === value;
            },
            render: (_: any, { id, status, message }: ITableData) => (
                <StatusTag
                    status={status}
                    message={message}
                    key={`tag-${id}`}
                />
            ),
        },
        {
            title: 'Data de Emissão',
            dataIndex: 'date_issue',
            key: 'date_issue',
            defaultSortOrder: 'descend' as keyof SortOrder,
            sorter: (a: ITableData, b: ITableData) => new Date(a?.date_issue!).getTime() - new Date(b?.date_issue!).getTime(),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({dataIndex: 'date_issue'}),
            render: (data: any) => (
                data ? formatDateStringToDateAndTime(data) : ''
            ),
        },
    ].filter((column) => !column?.hidden);

    const setTableDataFromRequest = useCallback((data: Array<IInvoice>) => {
        const newData = data.map((invoice) => ({
            ...invoice,
            key: invoice.id,
        }));

        setTableData(newData);
    }, []);

    const onClickTableRow = useCallback((record: any, index: any) => {
        if (selectedIds.includes(record.id)) {
            setSelectedIds((prevState) => prevState.filter((id) => id !== record.id));
        } else {
            setSelectedIds((prevState) => [...prevState, record.id]);
        }
    }, [selectedIds]);

    const onSelectChange = useCallback((newSelectedRowKeys: Array<any>) => {
        if (newSelectedRowKeys.length > selectedIds.length) {
            if (newSelectedRowKeys.length - 1 === selectedIds.length) {
                const newKey = newSelectedRowKeys.pop();
                return setSelectedIds((prevState) => [...prevState, newKey]);
            }

            if (newSelectedRowKeys.length - 1 > selectedIds.length) {
                return setSelectedIds(newSelectedRowKeys);
            }
        }

        setSelectedIds(newSelectedRowKeys);
    }, [selectedIds]);

    const rowSelection = useMemo(() => ({
        type: 'checkbox' as const,
        selectedRowKeys: selectedIds,
        onChange: onSelectChange,
    }), [selectedIds, onSelectChange]);


    const handleClickGoBack = useCallback(() => {
        window.history.back();
    }, []);

    useEffect(() => {
        if (!paramsPeriod) {
            return;
        }

        const controller = new AbortController();

        async function loadInvoices() {
            try {
                setIsLoading(true);

                const [month, year] = paramsPeriod?.split('-')!;
                setPeriod({ month: Number(month), year: Number(year) });

                const appUserId = Number(id);
                const cityId = activeCity?.id!;
                const signal = controller.signal;

                const [invoicesList, appUserData] = await Promise.all([
                    InvoicesService.listInvoicesByMonth({
                        appUserId,
                        cityId,
                        month: Number(month),
                        year: Number(year),
                        signal,
                    }),
                    AppUsersService.getAppUserById({appUserId, signal}),
                ])

                setTableDataFromRequest(invoicesList);
                setAppUser(appUserData);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'viewAppUserInvoices_asodkaskldasd');
                setIsLoading(false);
            }
        }

        loadInvoices();

        return () => {
            controller.abort();
        }
    }, [id, paramsPeriod, activeCity, setTableDataFromRequest]);

    return {
        columns,
        isLoading,
        tableData,
        appUser,
        onClickTableRow,
        rowSelection,
        selectedIds,
        period,
        handleClickGoBack,
    }
}
